/**
 * IapPaywall — Apple In-App Purchase plan list (iOS native only).
 * Renders StoreKit prices via RevenueCat. Required by App Review 3.1.1:
 * subscriptions must be purchasable via IAP on every storefront.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { iapSupported, getIapPackages, purchaseIapPackage, restoreIapPurchases } from "@/lib/revenuecat";

const PKG_META = {
  elite_monthly: { title: "Kennel Elite", blurb: "Top placement, elite kennel tools, lowest platform fee", period: "/mo", term: "1 month" },
  pro_monthly: { title: "Breeder Pro", blurb: "Unlimited registrations, listings & analytics", period: "/mo", term: "1 month" },
  basic_monthly: { title: "Breeder Basic", blurb: "10 dogs + 4 litters/month, kennel page, heat tracker", period: "/mo", term: "1 month" },
  buyer_pass_monthly: { title: "Buyer Pass", blurb: "Unlimited breeder messaging + full pedigrees", period: "/mo", term: "1 month" },
  buyer_pass_annual: { title: "Buyer Pass — Annual", blurb: "A full year of Buyer Pass", period: "/yr", term: "1 year" },
  doglog_monthly: { title: "Dog Log", blurb: "Daily care diary for your dog", period: "/mo", term: "1 month" },
};

export default function IapPaywall() {
  const [packages, setPackages] = useState([]);
  const [userId, setUserId] = useState(null);
  const [busy, setBusy] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!iapSupported()) return;
    let cancelled = false;
    (async () => {
      try {
        const me = await api.get("/auth/me", { silent: true });
        if (cancelled || !me?.data?.id) return;
        setUserId(me.data.id);
        const pkgs = await getIapPackages(me.data.id);
        if (!cancelled) setPackages(pkgs);
      } catch (e) {
        // StoreKit unavailable (simulator, no products yet) — render nothing
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!iapSupported() || !loaded || packages.length === 0) return null;

  const buy = async (pkg) => {
    setBusy(pkg.identifier);
    try {
      await purchaseIapPackage(userId, pkg);
      toast.success("Purchase complete — your plan is now active.");
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      const msg = String(e?.message || "");
      if (!/cancel/i.test(msg)) toast.error(msg || "Purchase failed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const restore = async () => {
    setBusy("restore");
    try {
      await restoreIapPurchases(userId);
      toast.success("Purchases restored.");
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error("Restore failed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="mb-8 rounded-2xl border border-[#E5E0D5] bg-white p-5" data-testid="iap-paywall">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Subscribe via App Store</p>
      <p className="text-[13px] text-[#6B6B6B] mt-1">
        Billed through your Apple ID. Manage or cancel any time in Settings → Subscriptions.
      </p>
      <div className="mt-4 space-y-2">
        {packages.map((pkg) => {
          const meta = PKG_META[pkg.identifier] || { title: pkg.product?.title || pkg.identifier, blurb: "" };
          return (
            <button
              key={pkg.identifier}
              type="button"
              disabled={Boolean(busy)}
              onClick={() => buy(pkg)}
              className="w-full flex items-center justify-between gap-3 rounded-xl border border-[#E5E0D5] hover:border-ink px-4 py-3 text-left transition-colors disabled:opacity-60"
              data-testid={`iap-buy-${pkg.identifier}`}
            >
              <span>
                <span className="block text-[15px] font-semibold text-ink">{meta.title}</span>
                {meta.blurb && <span className="block text-[12px] text-[#6B6B6B]">{meta.blurb}</span>}
                {meta.term && (
                  <span className="block text-[11px] text-[#9CA3AF] mt-0.5">
                    {meta.term}, auto-renews until cancelled
                  </span>
                )}
              </span>
              <span className="shrink-0 rounded-full bg-ink text-white text-[13px] font-semibold px-4 py-2">
                {busy === pkg.identifier ? "…" : `${pkg.product?.priceString || ""}${meta.period || ""}`}
              </span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        disabled={Boolean(busy)}
        onClick={restore}
        className="mt-3 text-[13px] font-semibold text-[#6B6B6B] underline underline-offset-2"
        data-testid="iap-restore-btn"
      >
        {busy === "restore" ? "Restoring…" : "Restore Purchases"}
      </button>
      <p className="mt-3 text-[11px] text-[#9CA3AF] leading-relaxed" data-testid="iap-legal-note">
        Payment is charged to your Apple&nbsp;ID at confirmation. Subscriptions
        auto-renew at the shown price until cancelled at least 24 hours before
        the end of the period, in Settings &rarr; your name &rarr; Subscriptions.{" "}
        <Link to="/terms" className="underline underline-offset-2" data-testid="iap-terms-link">Terms of Use</Link>
        {" · "}
        <Link to="/privacy" className="underline underline-offset-2" data-testid="iap-privacy-link">Privacy Policy</Link>
      </p>
    </section>
  );
}
